import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

/**
 * M10 L10.07 — Evidence Request (§14).
 *
 * Requests additional evidence from the painter/vendor for a review via
 * `POST /v1/quality/reviews/:reviewId/evidence` with one or more requested kinds,
 * instructions and an optional deadline. Outstanding/answered requests are
 * listed from `GET .../evidence`.
 */
const EVIDENCE_KINDS = ['PHOTO', 'VIDEO', 'DOCUMENT', 'GPS', 'INVOICE', 'OTHER'] as const;

interface EvidenceRow {
  readonly evidenceRequestId: string;
  readonly requestedKinds: readonly string[] | null;
  readonly instructions: string | null;
  readonly status: string;
  readonly deadlineAt: string | null;
}

export function QualityEvidencePage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<readonly EvidenceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [kinds, setKinds] = useState<Record<string, boolean>>({});
  const [instructions, setInstructions] = useState('');
  const [deadlineAt, setDeadlineAt] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<readonly EvidenceRow[]>(`/v1/quality/reviews/${reviewId}/evidence`, { token });
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const selectedKinds = EVIDENCE_KINDS.filter((k) => kinds[k]);

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/quality/reviews/${reviewId}/evidence`, {
        method: 'POST', token,
        body: {
          requestedKinds: selectedKinds,
          instructions: instructions.trim(),
          deadlineAt: deadlineAt ? new Date(deadlineAt).toISOString() : undefined,
        },
      });
      setKinds({}); setInstructions(''); setDeadlineAt('');
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, selectedKinds, instructions, deadlineAt, load]);

  const invalid = selectedKinds.length === 0 || !instructions.trim();

  return (
    <main aria-label="quality-evidence">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.evidence.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form onSubmit={onSubmit} aria-label="evidence-request-form" style={{ maxWidth: 520, marginBottom: 24 }}>
        <fieldset aria-label="evidence-kinds" style={{ border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <legend style={{ fontSize: 13, fontWeight: 600 }}>{t('quality.evidence.kindsLegend')}</legend>
          {EVIDENCE_KINDS.map((k) => (
            <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginRight: 12 }}>
              <input type="checkbox" data-testid={`ev-kind-${k}`} checked={!!kinds[k]}
                     onChange={(e) => setKinds((c) => ({ ...c, [k]: e.target.checked }))} />
              {k}
            </label>
          ))}
        </fieldset>

        <label htmlFor="ev-instructions" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{t('quality.evidence.instructions')}</label>
        <textarea id="ev-instructions" data-testid="ev-instructions" value={instructions} rows={3} required
                  onChange={(e) => setInstructions(e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)', width: '100%', marginBottom: 12 }} />

        <label htmlFor="ev-deadline" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{t('quality.evidence.deadline')}</label>
        <DateInput id="ev-deadline" data-testid="ev-deadline" type="datetime-local" value={deadlineAt}
               onValueChange={(nextValue) => setDeadlineAt(nextValue)}
               style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)', marginBottom: 12, display: 'block' }} />

        <Button type="submit" data-testid="ev-submit" disabled={busy || invalid} variant="primary">
          {busy ? t('quality.evidence.requesting') : t('quality.evidence.request')}
        </Button>
        {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 8 }}>{actionError}</p>}
      </form>

      <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.evidence.requestsTitle')}</h2>
      {rows.length === 0 ? (
        <p data-testid="ev-empty" style={{ color: 'var(--color-text-muted)' }}>{t('quality.evidence.empty')}</p>
      ) : (
        <DataTable<EvidenceRow>
          testId="ev-table"
          rowKey={(r) => r.evidenceRequestId}
          rowTestId={(r) => `ev-row-${r.evidenceRequestId}`}
          columns={[
            { key: 'kinds', header: t('quality.evidence.col.kinds'), render: (r) => (r.requestedKinds ?? []).join(', ') || '—' },
            { key: 'status', header: t('quality.common.status'), render: (r) => r.status },
            { key: 'deadline', header: t('quality.evidence.col.deadline'), render: (r) => (r.deadlineAt ? at(r.deadlineAt) : '—') },
          ]}
          rows={rows}
        />
      )}
    </main>
  );
}
